// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useQuery } from "@tanstack/react-query";
import { atom } from "jotai";

import { type ApiError, parseResponseError } from "../infra/apiError";
import { fetchWithTimeout } from "../infra/fetchWithTimeout";
import { jotaiStore } from "../jotaiStore";
import type { User } from "./atoms";
import { minifyGraphQLQuery } from "./backendai";

export const CURRENT_USER_QUERY_KEY = ["currentUser"];

/**
 * The last error encountered while fetching the current user, or `null` when
 * the most recent fetch succeeded. Updated in-place by `fetchCurrentUser` so
 * the query itself never enters React Query's `error` state — see the notes
 * on `useCurrentUser` below for why that matters.
 */
export const lastCurrentUserErrorAtom = atom<ApiError | null>(null);

// The app mounts a `<Provider store={jotaiStore}>` at the root (see
// `index.tsx`), so non-React writers MUST go through the same store — not
// `getDefaultStore()`, which is a completely separate instance and silently
// fails to notify `useAtomValue` consumers inside the provider. See #3382.
function setLastError(error: ApiError | null) {
  jotaiStore.set(lastCurrentUserErrorAtom, error);
}

/**
 * Fetches the current authenticated user via the Backend.AI admin GraphQL API.
 *
 * **Never throws.** Every failure path — 401/403/409/429/5xx, network errors,
 * JSON parse errors — resolves to `null` and records a structured `ApiError`
 * in `lastCurrentUserErrorAtom`. A successful fetch clears the atom.
 *
 * Why: React Query v5 fires `executeFetch` on every new observer subscribe
 * when the query is in `status === 'error'` with no cached data, regardless
 * of `refetchOnMount`. With ~11 `useCurrentUser` consumers × React StrictMode
 * double-mount, a single transient failure otherwise produces an unbounded
 * stream of refetches (and toasts). Keeping the query perpetually in a
 * "resolved" state (data may be `null`) breaks that cycle.
 *
 * Callers distinguish "not authenticated" from "transient failure" by
 * combining `data` with `lastCurrentUserErrorAtom`:
 * - `data != null` → authenticated
 * - `data == null && lastError == null` → unauthenticated (no credentials)
 * - `data == null && lastError.status in {401, 403}` → unauthenticated
 * - `data == null && lastError` otherwise → transient failure, keep page
 *
 * `baiFetch` (used internally by `fetchWithTimeout`) attaches the
 * `Authorization: Bearer` header from the stored token.
 */
async function fetchCurrentUser(): Promise<User | null> {
  let response: Response;
  try {
    response = await fetchWithTimeout("/func/admin/gql", {
      method: "POST",
      body: JSON.stringify({
        query: minifyGraphQLQuery(`
          query {
            user {
              username
              email
              full_name
              is_active
              role
              domain_name
              groups {
                id
                name
              }
              resource_policy
            }
          }
        `),
      }),
    });
  } catch (networkError) {
    // Network failure, timeout, abort — record and resolve to null.
    setLastError({
      message:
        networkError instanceof Error
          ? networkError.message
          : "Network error — please check your connection",
      endpoint: "/func/admin/gql",
    });
    return null;
  }

  if (response.status === 401 || response.status === 403) {
    setLastError({
      message: "Unauthenticated",
      status: response.status,
      endpoint: "/func/admin/gql",
    });
    return null;
  }

  if (!response.ok) {
    const apiError = await parseResponseError(response, "/func/admin/gql");
    setLastError(apiError);
    return null;
  }

  try {
    const json = (await response.json()) as { data: { user: User } };
    const user = json?.data?.user ?? null;
    setLastError(null);
    return user;
  } catch (parseError) {
    setLastError({
      message:
        parseError instanceof Error
          ? parseError.message
          : "Failed to parse current-user response",
      endpoint: "/func/admin/gql",
    });
    return null;
  }
}

/**
 * React Query hook that replaces the Jotai `userAtom` for auth state.
 *
 * Contract (see `fetchCurrentUser` for details):
 * - The query **never** enters `isError`. All failures resolve to `data == null`
 *   and surface through `lastCurrentUserErrorAtom`.
 * - `data` is the `User` object when authenticated, `null` otherwise.
 * - `isLoading` is `true` during the initial fetch (no cached data yet).
 * - Cached for 5 minutes (`staleTime`) to avoid redundant auth checks.
 * - `retry: false` — retrying a rate limit from the client worsens it.
 */
export function useCurrentUser() {
  return useQuery<User | null>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    retry: false,
    staleTime: 5 * 60_000,
  });
}
